"use client";

import { useState } from "react";
import { useT } from "@tillix/i18n/react";
import { Button } from "@tillix/ui-atoms/button";
import {
  FieldControl,
  FieldError,
  FieldLabel,
  FieldRoot,
} from "@tillix/ui-atoms/field";
import { Input } from "@tillix/ui-atoms/input";
import { Loader2, Mail } from "lucide-react";
import { observer } from "mobx-react-lite";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface QuickPayRegisterFormProps {
  defaultEmail?: string;
  defaultName: string;
  onSubmit: (data: { email: string }) => Promise<void>;
}

function QuickPayRegisterForm({
  defaultEmail = "",
  defaultName,
  onSubmit,
}: QuickPayRegisterFormProps) {
  const { t } = useT();
  const [email, setEmail] = useState(defaultEmail);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isValidEmail = EMAIL_REGEX.test(email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!isValidEmail) {
      setError(t("quickPay.validEmailRequired"));
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({ email: email.trim() });
    } catch {
      setError(t("quickPay.registrationFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
      <p className="text-muted-foreground text-sm">
        {t("quickPay.registerAs")}{" "}
        <span className="font-medium text-foreground">{defaultName}</span>
      </p>

      <FieldRoot name="email">
        <FieldLabel>{t("quickPay.emailAddress")}</FieldLabel>
        <FieldControl
          render={(defaultProps) => (
            <Input
              {...defaultProps}
              autoComplete="email"
              autoFocus={true}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null);
              }}
              placeholder="you@example.com"
              type="email"
              value={email}
            />
          )}
        />
        <FieldError>{error}</FieldError>
      </FieldRoot>

      <Button
        className="w-full"
        disabled={!isValidEmail || isSubmitting}
        type="submit"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 size-4 animate-spin" />
            {t("quickPay.sendingLink")}
          </>
        ) : (
          <>
            <Mail className="mr-2 size-4" />
            {t("quickPay.sendSignInLink")}
          </>
        )}
      </Button>
    </form>
  );
}

export default observer(QuickPayRegisterForm);
